import React, { useEffect, useState } from "react";
import api, { formatErr, money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Printer, Download, Eye, PencilSimple, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { exportToCSV } from "@/lib/csvExport";
import ItemSearchSelect from "@/components/ItemSearchSelect";
import SelectBox from "@/components/SelectBox";
import CustomerPicker, { ensureCustomer } from "@/components/CustomerPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function TransactionForm({ kind }) {
  // kind = 'purchase' or 'sale'
  const isPurchase = kind === "purchase";
  const label = isPurchase ? "Purchase" : "Sale / Invoice";
  const partyEndpoint = isPurchase ? "/vendors" : "/customers";
  const partyField = isPurchase ? "vendor_id" : "customer_id";
  const dateField = isPurchase ? "purchase_date" : "sale_date";
  const listEndpoint = isPurchase ? "/purchases" : "/sales";
  const testid = kind;

  const [parties, setParties] = useState([]);
  const [rows, setRows] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10)); // Purchase: vendor bill date
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceType, setInvoiceType] = useState("TAX_INVOICE"); // Sale only: TAX_INVOICE | CASH_MEMO
  const [lines, setLines] = useState([{ item_id: "", qty: 1, price: 0 }]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [companyState, setCompanyState] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [saleTechId, setSaleTechId] = useState("");
  const [commissionRate, setCommissionRate] = useState(10);
  const [paymentMode, setPaymentMode] = useState("ONLINE");
  const [editId, setEditId] = useState(null);  // sale id when editing
  const [shipToOpen, setShipToOpen] = useState(false);
  const [shipTo, setShipTo] = useState({ name: "", address: "", city: "", state: "", mobile: "", gst: "" });
  const [search, setSearch] = useState("");
  const [discountType, setDiscountType] = useState("NONE");
  const [discountValue, setDiscountValue] = useState(0);
  const [newCust, setNewCust] = useState(null); // inline new customer (sale only)
  const navG = useNavigate();

  const load = async () => {
    // Load list + parties first (fast) — don't load 22K items into memory
    try {
      const [p, l] = await Promise.all([api.get(partyEndpoint), api.get(listEndpoint)]);
      setParties(p.data); setRows(l.data);
    } catch {}
    if (!isPurchase) {
      api.get("/technicians").then(r => setTechnicians(r.data || [])).catch(() => {});
    }
    api.get("/config").then(r => setCompanyState((r.data.company_state || "").toLowerCase())).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const viewDetail = async (r) => {
    try {
      const { data } = await api.get(`${listEndpoint}/${r.id}`);
      setDetailData(data);
      setDetailOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeRow = async (r) => {
    if (!window.confirm(`Delete ${label} ${r.invoice_no || ""}? Stock will be rolled back.`)) return;
    try {
      await api.delete(`${listEndpoint}/${r.id}`);
      toast.success(`${label} deleted`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // WhatsApp share — customer ke mobile par public invoice link bhejta hai
  const shareWhatsApp = async (r) => {
    try {
      // fetch fresh row to get customer
      const { data } = await api.get(`${listEndpoint}/${r.id}`);
      const cust = data.customer || {};
      const mobile = (cust.mobile || "").replace(/\D/g, "");
      if (!mobile) {
        toast.error("Customer ka mobile number nahi hai. Pehle customer master me add karein.");
        return;
      }
      const num = mobile.length === 10 ? `91${mobile}` : mobile;
      const name = cust.customer_name || cust.first_name || "Customer";
      const shareUrl = `${window.location.origin}/public/invoice/${r.id}`;
      const total = money(data.total_amount || 0);
      const msg =
        `Namaste ${name},\n\n` +
        `Aapka Invoice *${data.invoice_no}* — Total: *${total}*\n\n` +
        `View / Download: ${shareUrl}\n\n` +
        `Thank you,\nShree Ram Enterprises`;
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };

  // auto-fetch next invoice number when opening sale form
  const openForm = async () => {
    setEditId(null);
    setPartyId(""); setInvoiceNo(""); setRemarks("");
    setDate(new Date().toISOString().slice(0, 10));
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setInvoiceType("TAX_INVOICE");
    setSaleTechId(""); setCommissionRate(10); setPaymentMode("ONLINE");
    setLines([{ item_id: "", qty: 1, price: 0 }]);
    setShipTo({ name: "", address: "", city: "", state: "", mobile: "", gst: "" });
    setShipToOpen(false);
    setDiscountType("NONE"); setDiscountValue(0);
    setNewCust(null);
    if (!isPurchase && !invoiceNo) {
      try {
        const { data } = await api.get("/sales/next-invoice-no");
        setInvoiceNo(data.invoice_no);
      } catch {}
    }
    setShowForm(true);
  };

  // When user toggles Tax Invoice ⟷ Cash Memo, fetch the correct next number
  const changeInvoiceType = async (newType) => {
    setInvoiceType(newType);
    if (editId) return; // don't overwrite invoice_no when editing existing
    try {
      const url = newType === "CASH_MEMO" ? "/sales/next-cash-memo-no" : "/sales/next-invoice-no";
      const { data } = await api.get(url);
      setInvoiceNo(data.invoice_no);
    } catch {}
  };

  const openEditSale = async (r) => {
    try {
      const { data } = await api.get(`${listEndpoint}/${r.id}`);
      setEditId(data.id);
      setNewCust(null);
      setPartyId(data[partyField] || "");
      setDate(data[dateField] || new Date().toISOString().slice(0, 10));
      setInvoiceDate(data.invoice_date || data[dateField] || new Date().toISOString().slice(0, 10));
      setInvoiceNo(data.invoice_no || "");
      setInvoiceType(data.invoice_type || "TAX_INVOICE");
      setRemarks(data.remarks || "");
      if (!isPurchase) {
        setSaleTechId(data.technician_id || "");
        setCommissionRate(data.commission_rate ?? 10);
        setPaymentMode(data.payment_mode || "ONLINE");
      }
      setLines((data.items || []).map(li => ({
        item_id: li.item_id,
        qty: li.qty,
        price: li.price,
        item: {
          id: li.item_id,
          item_code: li.item_code,
          item_name: li.item_name,
          brand: li.brand,
          gst_rate: li.gst_rate,
          item_type: li.item_type || "GOODS",
          sale_price: li.price,
          current_stock: null,
        },
      })));
      const st = {
        name: data.ship_to_name || "", address: data.ship_to_address || "",
        city: data.ship_to_city || "", state: data.ship_to_state || "",
        mobile: data.ship_to_mobile || "", gst: data.ship_to_gst || "",
      };
      setShipTo(st);
      setShipToOpen(!!(st.name || st.address || st.city || st.state || st.mobile || st.gst));
      setDiscountType(data.discount_type || "NONE");
      setDiscountValue(data.discount_value || 0);
      setShowForm(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const selectedParty = parties.find(p => p.id === partyId);
  const partyState = ((newCust ? newCust.state : selectedParty?.state) || "").toLowerCase();
  const hasStates = !!companyState && !!partyState;
  const isIntra = hasStates && companyState === partyState;
  const taxLabel = isIntra ? "CGST+SGST" : "IGST";

  const exportExpanded = async () => {
    try {
      const { data } = await api.get(`${listEndpoint}/export`);
      if (!data.length) return toast.error("No data to export");
      const cols = Object.keys(data[0]).map(k => ({ label: k.toUpperCase().replace(/_/g, " "), key: k }));
      exportToCSV(data, cols, isPurchase ? "purchases_detailed" : "sales_detailed");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exportGST = async () => {
    try {
      const { data } = await api.get(`${listEndpoint}/export-gst`);
      if (!data.length) return toast.error("No data to export");
      const cols = Object.keys(data[0]).map(k => ({ label: k, key: k }));
      exportToCSV(data, cols, isPurchase ? "purchases_GST_CA_format" : "sales_GST_CA_format");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const addLine = () => setLines([...lines, { item_id: "", qty: 1, price: 0 }]);
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updLine = (i, key, val, itemObj = null) => {
    const nl = [...lines];
    nl[i] = { ...nl[i], [key]: val };
    if (key === "item_id" && itemObj) {
      nl[i].item = itemObj;
      nl[i].price = isPurchase ? (itemObj.purchase_price ?? 0) : (itemObj.sale_price ?? 0);
    } else if (key === "item_id" && !itemObj) {
      nl[i].item = null;
    }
    setLines(nl);
  };

  const totals = (() => {
    const isCashMemo = !isPurchase && invoiceType === "CASH_MEMO";
    const raw = lines.reduce((acc, l) => {
      const gst = isCashMemo ? 0 : (l.item ? Number(l.item.gst_rate || 0) : 0);
      const amt = Number(l.qty || 0) * Number(l.price || 0);
      const goods = (l.item?.item_type || "GOODS").toUpperCase() !== "SERVICE";
      acc.sub += amt;
      if (goods) acc.goods += amt;
      acc._lineGst.push({ amt, gst, goods });
      return acc;
    }, { sub: 0, goods: 0, _lineGst: [] });
    // discount
    let disc = 0;
    const dv = Number(discountValue || 0);
    // Discount only on parts (GOODS), never on service charges
    if (discountType === "PERCENT") disc = raw.goods * dv / 100;
    else if (discountType === "FLAT") disc = Math.min(dv, raw.goods);
    disc = Math.max(0, disc);
    const taxable = raw.sub - disc;
    const scale = raw.goods > 0 ? (raw.goods - disc) / raw.goods : 1;
    const tax = raw._lineGst.reduce((s, x) => s + x.amt * (x.goods ? scale : 1) * x.gst / 100, 0);
    const preRound = taxable + tax;
    // Sale + Quote apply round-off; Purchase keeps paisa precision
    const doRound = !isPurchase;
    const total = doRound ? Math.round(preRound) : preRound;
    const roundOff = doRound ? (total - preRound) : 0;
    return { sub: raw.sub, discount: disc, taxable, tax, preRound, roundOff, total, scale };
  })();

  const save = async () => {
    if (newCust && !newCust.customer_name.trim()) return toast.error("New customer ka naam daalein");
    if (!newCust && !partyId) return toast.error(`Select ${isPurchase ? "vendor" : "customer"}`);
    if (isPurchase) {
      if (!invoiceNo.trim()) return toast.error("Bill / Invoice No is required");
      if (!invoiceDate) return toast.error("Bill Date is required");
    }
    if (!lines.length || lines.some(l => !l.item_id || !l.qty)) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      let pid = partyId;
      if (newCust) {
        pid = await ensureCustomer(newCust, parties);
        setNewCust(null); setPartyId(pid);
        api.get(partyEndpoint).then(r => setParties(r.data)).catch(() => {});
      }
      const payload = {
        [partyField]: pid,
        [dateField]: date,
        invoice_no: invoiceNo,
        items: lines.map(l => ({ item_id: l.item_id, qty: parseFloat(l.qty), price: parseFloat(l.price) })),
        remarks,
        discount_type: discountType,
        discount_value: parseFloat(discountValue) || 0,
      };
      if (isPurchase) {
        payload.invoice_date = invoiceDate;
      }
      if (!isPurchase) {
        payload.invoice_type = invoiceType;
      }
      if (!isPurchase) {
        payload.technician_id = saleTechId || "";
        payload.commission_rate = parseFloat(commissionRate) || 0;
        payload.payment_mode = paymentMode;
        payload.ship_to_name = shipTo.name;
        payload.ship_to_address = shipTo.address;
        payload.ship_to_city = shipTo.city;
        payload.ship_to_state = shipTo.state;
        payload.ship_to_mobile = shipTo.mobile;
        payload.ship_to_gst = shipTo.gst;
      }
      if (editId) {
        await api.put(`${listEndpoint}/${editId}`, payload);
        toast.success(`${label} updated`);
      } else {
        await api.post(listEndpoint, payload);
        toast.success(`${label} saved`);
      }
      setShowForm(false);
      setEditId(null);
      setPartyId(""); setInvoiceNo(""); setRemarks("");
      setSaleTechId(""); setCommissionRate(10); setPaymentMode("ONLINE");
      setInvoiceType("TAX_INVOICE");
      setShipTo({ name: "", address: "", city: "", state: "", mobile: "", gst: "" });
      setShipToOpen(false);
      setDiscountType("NONE"); setDiscountValue(0);
      setNewCust(null);
      setLines([{ item_id: "", qty: 1, price: 0 }]);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`${testid}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">{label}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isPurchase ? "Record parts received from vendors." : "Create sales invoices with GST calculation."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportExpanded} data-testid={`${testid}-export`}>
            <Download size={14} className="mr-1" /> Export CSV (with items)
          </Button>
          <Button variant="outline" onClick={exportGST} data-testid={`${testid}-export-gst`} className="border-green-300 text-green-700 hover:bg-green-50">
            <Download size={14} className="mr-1" /> Export GST (CA Format)
          </Button>
          <Button onClick={openForm} data-testid={`${testid}-toggle`} className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
            <Plus size={16} className="mr-1" weight="bold" /> New {isPurchase ? "Purchase" : "Invoice"}
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="p-6 border border-border">
          <h3 className="font-display font-semibold mb-4">{editId ? `Edit ${label}` : `New ${label}`}{editId && invoiceNo && <span className="text-sm text-muted-foreground ml-2">— {invoiceNo}</span>}</h3>

          {!isPurchase && (
            <div className="mb-4 p-3 rounded border border-border bg-slate-50/60" data-testid="invoice-type-toggle">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Invoice Type</Label>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => changeInvoiceType("TAX_INVOICE")}
                  data-testid="sale-type-tax"
                  className={`flex-1 px-4 py-2.5 rounded-md border-2 text-sm font-semibold transition ${
                    invoiceType === "TAX_INVOICE"
                      ? "bg-[hsl(24.6_95%_53.1%)] text-white border-[hsl(24.6_95%_53.1%)]"
                      : "bg-white text-muted-foreground border-border hover:border-[hsl(24.6_95%_53.1%)]"
                  }`}
                >
                  🧾 Tax Invoice <span className="text-[10px] font-normal opacity-80 ml-1">(GST bill, filing me use)</span>
                </button>
                <button
                  type="button"
                  onClick={() => changeInvoiceType("CASH_MEMO")}
                  data-testid="sale-type-cash"
                  className={`flex-1 px-4 py-2.5 rounded-md border-2 text-sm font-semibold transition ${
                    invoiceType === "CASH_MEMO"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-muted-foreground border-border hover:border-emerald-600"
                  }`}
                >
                  💵 Cash Memo <span className="text-[10px] font-normal opacity-80 ml-1">(no GST, GST filing me nahi)</span>
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className={newCust ? "md:col-span-3" : ""}>
              <Label>{isPurchase ? "Vendor" : "Customer"}*</Label>
              {isPurchase ? <SelectBox value={partyId} onChange={setPartyId} testid={`${testid}-party`}
                placeholder="Select vendor"
                options={parties.map(p => ({ value: p.id, label: p.vendor_name }))} /> :
              <CustomerPicker value={partyId} parties={parties} newCust={newCust} setNewCust={setNewCust} disableNew={!!editId} onChange={(id) => {
                setPartyId(id);
                if (!isPurchase && id) {
                  const c = parties.find(p => p.id === id);
                  if (c) {
                    // Auto-fill Ship-To from customer's saved shipping address
                    const st = {
                      name: c.shipping_attention || c.customer_name || "",
                      address: [c.shipping_street1, c.shipping_street2].filter(Boolean).join(", "),
                      city: c.shipping_city || "",
                      state: c.shipping_state || "",
                      mobile: c.mobile || "",
                      gst: c.gst_no || "",
                    };
                    // Only overwrite if user hasn't already typed something (editing mode: skip)
                    if (!editId && (st.address || st.city)) {
                      setShipTo(st);
                    }
                  }
                }
              }} testid={`${testid}-party`} />}
            </div>
            <div>
              <Label>{isPurchase ? "Entry Date" : "Date"}</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid={`${testid}-date`} />
            </div>
            <div>
              <Label>
                {isPurchase ? (
                  <>Bill / Invoice No <span className="text-red-600">*</span></>
                ) : (
                  <>Invoice / Ref No (auto — editable)</>
                )}
              </Label>
              <Input value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} data-testid={`${testid}-invoice`} />
            </div>
          </div>

          {isPurchase && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 rounded border border-amber-200 bg-amber-50/40">
              <div>
                <Label>
                  Bill Date (Vendor Invoice Date) <span className="text-red-600">*</span>
                </Label>
                <Input type="date" value={invoiceDate}
                  onChange={e => setInvoiceDate(e.target.value)}
                  data-testid="purchase-bill-date" />
                <div className="text-xs text-muted-foreground mt-1">
                  Vendor ke bill par jo date likhi hai
                </div>
              </div>
            </div>
          )}

          {!isPurchase && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 rounded border border-border bg-blue-50/40">
              <div>
                <Label>Assigned Technician (optional)</Label>
                <select value={saleTechId} onChange={e => setSaleTechId(e.target.value)}
                  className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="sale-tech">
                  <option value="">— None —</option>
                  {technicians.map(t => <option key={t.id} value={t.id}>{t.technician_name}</option>)}
                </select>
              </div>
              <div>
                <Label>Commission % {saleTechId && <span className="text-xs text-muted-foreground">(auto entry in tech ledger)</span>}</Label>
                <Input type="number" value={commissionRate} onChange={e => setCommissionRate(e.target.value)} disabled={!saleTechId} data-testid="sale-commission" />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <select value={paymentMode} onChange={e => setPaymentMode(e.target.value)}
                  className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="sale-payment-mode">
                  <option value="ONLINE">Online (to our account)</option>
                  <option value="CASH">Cash (technician collects)</option>
                </select>
              </div>
            </div>
          )}

          {!isPurchase && (
            <div className="mb-4 border border-border rounded">
              <button type="button" onClick={() => setShipToOpen(!shipToOpen)}
                className="w-full flex items-center justify-between px-4 py-2 text-sm font-semibold bg-amber-50/60 hover:bg-amber-50 rounded-t"
                data-testid="sale-shipto-toggle">
                <span>📦 Ship To (delivery address) — {shipToOpen ? "hide" : (shipTo.name || shipTo.address ? "edit" : "add if different from Bill To")}</span>
                <span className="text-xs text-muted-foreground">{shipToOpen ? "▲" : "▼"}</span>
              </button>
              {shipToOpen && (
                <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Consignee Name</Label>
                    <Input value={shipTo.name} onChange={e => setShipTo({ ...shipTo, name: e.target.value })} data-testid="sale-shipto-name" placeholder="e.g. Site engineer / Store" />
                  </div>
                  <div>
                    <Label>Mobile</Label>
                    <Input value={shipTo.mobile} onChange={e => setShipTo({ ...shipTo, mobile: e.target.value })} data-testid="sale-shipto-mobile" />
                  </div>
                  <div>
                    <Label>GSTIN (if any)</Label>
                    <Input value={shipTo.gst} onChange={e => setShipTo({ ...shipTo, gst: e.target.value })} data-testid="sale-shipto-gst" />
                  </div>
                  <div className="md:col-span-3">
                    <Label>Address</Label>
                    <Input value={shipTo.address} onChange={e => setShipTo({ ...shipTo, address: e.target.value })} data-testid="sale-shipto-address" placeholder="Delivery / installation address" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={shipTo.city} onChange={e => setShipTo({ ...shipTo, city: e.target.value })} data-testid="sale-shipto-city" />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input value={shipTo.state} onChange={e => setShipTo({ ...shipTo, state: e.target.value })} data-testid="sale-shipto-state" />
                  </div>
                  <div className="flex items-end">
                    <Button variant="outline" size="sm" onClick={() => setShipTo({ name: "", address: "", city: "", state: "", mobile: "", gst: "" })} data-testid="sale-shipto-clear">
                      Clear
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {partyId && (
            <div className="mb-4 p-3 rounded border border-border bg-secondary/30 text-sm">
              <span className="font-semibold">Tax Type: </span>
              {hasStates ? (
                <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${isIntra ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                  {isIntra ? "CGST + SGST (Intra-State)" : "IGST (Inter-State)"}
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-yellow-100 text-yellow-800">
                  IGST (default — set state on {isPurchase ? "vendor" : "customer"} for proper split)
                </span>
              )}
              <span className="text-muted-foreground ml-2">
                Company: {companyState || "—"} | {isPurchase ? "Vendor" : "Customer"}: {partyState || "not set"}
              </span>
            </div>
          )}

          <div className="border border-border rounded overflow-visible">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="w-24">Qty</TableHead>
                  <TableHead className="w-32">Price</TableHead>
                  <TableHead className="w-20">GST%</TableHead>
                  <TableHead className="w-24 text-right">{taxLabel}</TableHead>
                  <TableHead className="w-32 text-right">Total</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, i) => {
                  const gst = (!isPurchase && invoiceType === "CASH_MEMO") ? 0 : (l.item ? Number(l.item.gst_rate || 0) : 0);
                  const amt = Number(l.qty || 0) * Number(l.price || 0) * ((l.item?.item_type || "GOODS").toUpperCase() !== "SERVICE" ? totals.scale : 1);
                  const total = amt + amt * gst / 100;
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <ItemSearchSelect
                          value={l.item_id}
                          onChange={(id, itemObj) => updLine(i, "item_id", id, itemObj)}
                          items={l.item ? [l.item] : []}
                          testid={`${testid}-line-item-${i}`}
                        />
                      </TableCell>
                      <TableCell><Input type="number" value={l.qty} onChange={e => updLine(i, "qty", e.target.value)} data-testid={`${testid}-line-qty-${i}`} /></TableCell>
                      <TableCell><Input type="number" value={l.price} onChange={e => updLine(i, "price", e.target.value)} data-testid={`${testid}-line-price-${i}`} /></TableCell>
                      <TableCell>{gst}%</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{money(amt * gst / 100)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(total)}</TableCell>
                      <TableCell>
                        <button onClick={() => rmLine(i)} className="p-1 text-destructive hover:bg-destructive/10 rounded" data-testid={`${testid}-line-remove-${i}`}><Trash size={15} /></button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <Button onClick={addLine} variant="outline" size="sm" className="mt-3" data-testid={`${testid}-add-line`}>
            <Plus size={14} className="mr-1" /> Add Line
          </Button>

          <div className="mt-6 flex justify-between items-end">
            <div className="w-96 space-y-3">
              <div>
                <Label>Remarks</Label>
                <Input value={remarks} onChange={e => setRemarks(e.target.value)} data-testid={`${testid}-remarks`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label title="Discount sirf parts par lagta hai, service charge par nahi">Discount Type <span className="text-[10px] text-muted-foreground font-normal">(parts only)</span></Label>
                  <select value={discountType} onChange={e => setDiscountType(e.target.value)}
                    className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid={`${testid}-discount-type`}>
                    <option value="NONE">No discount</option>
                    <option value="FLAT">Flat ₹ off</option>
                    <option value="PERCENT">% off</option>
                  </select>
                </div>
                <div>
                  <Label>{discountType === "PERCENT" ? "Discount %" : "Discount ₹"}</Label>
                  <Input type="number" value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                    disabled={discountType === "NONE"} data-testid={`${testid}-discount-value`} />
                </div>
              </div>
            </div>
            <div className="text-right space-y-1 min-w-[240px]">
              <div className="text-sm"><span className="text-muted-foreground mr-4">Subtotal:</span><span className="tabular-nums">{money(totals.sub)}</span></div>
              {totals.discount > 0 && (
                <>
                  <div className="text-sm text-orange-700"><span className="text-muted-foreground mr-4">Discount{discountType === "PERCENT" ? ` (${discountValue}%)` : ""}:</span><span className="tabular-nums">-{money(totals.discount)}</span></div>
                  <div className="text-sm"><span className="text-muted-foreground mr-4">Taxable:</span><span className="tabular-nums">{money(totals.taxable)}</span></div>
                </>
              )}
              {isPurchase || invoiceType !== "CASH_MEMO" ? (
                isIntra ? (
                  <>
                    <div className="text-sm"><span className="text-muted-foreground mr-4">CGST:</span><span className="tabular-nums">{money(totals.tax / 2)}</span></div>
                    <div className="text-sm"><span className="text-muted-foreground mr-4">SGST:</span><span className="tabular-nums">{money(totals.tax / 2)}</span></div>
                  </>
                ) : (
                  <div className="text-sm"><span className="text-muted-foreground mr-4">IGST:</span><span className="tabular-nums">{money(totals.tax)}</span></div>
                )
              ) : (
                <div className="text-xs text-emerald-700 font-semibold mt-1" data-testid="cash-memo-notice">💵 Cash Memo — No GST</div>
              )}
              {!isPurchase && Math.abs(totals.roundOff) > 0.001 && (
                <div className="text-sm text-blue-700" data-testid="txn-round-off">
                  <span className="text-muted-foreground mr-4">Round Off:</span>
                  <span className="tabular-nums">{totals.roundOff > 0 ? "+" : "-"}{money(Math.abs(totals.roundOff))}</span>
                </div>
              )}
              <div className="font-display text-2xl font-bold"><span className="text-muted-foreground text-sm mr-4">Total:</span>{money(totals.total)}</div>
            </div>
          </div>

          <div className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid={`${testid}-save`} className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
              {busy ? "Saving..." : (editId ? `Update ${label}` : `Save ${label}`)}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-0 border border-border overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-3 bg-secondary/30">
          <Input
            placeholder={`🔍 Search by invoice #, ${isPurchase ? "vendor" : "customer"} name, or date (YYYY-MM-DD)…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 h-9"
            data-testid={`${testid}-search`}
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-xs px-2 py-1 rounded hover:bg-secondary text-muted-foreground" data-testid={`${testid}-search-clear`}>
              Clear
            </button>
          )}
          <span className="text-xs text-muted-foreground tabular-nums">
            {(() => {
              const s = search.trim().toLowerCase();
              const filtered = !s ? rows : rows.filter(r =>
                (r.invoice_no || "").toLowerCase().includes(s) ||
                (isPurchase ? (r.vendor_name || "") : (r.customer_name || "")).toLowerCase().includes(s) ||
                (r[dateField] || "").toLowerCase().includes(s) ||
                (r.invoice_date || "").toLowerCase().includes(s) ||
                (r.remarks || "").toLowerCase().includes(s)
              );
              return `${filtered.length} of ${rows.length}`;
            })()}
          </span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{isPurchase ? "Entry Date" : "Date"}</TableHead>
              {isPurchase && <TableHead>Bill Date</TableHead>}
              <TableHead>Invoice #</TableHead>
              <TableHead>{isPurchase ? "Vendor" : "Customer"}</TableHead>
              <TableHead className="text-right">Sub-total</TableHead>
              <TableHead className="text-right">Tax</TableHead>
              <TableHead className="text-right">Grand Total</TableHead>
              <TableHead className="text-right w-24">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const s = search.trim().toLowerCase();
              const filtered = !s ? rows : rows.filter(r =>
                (r.invoice_no || "").toLowerCase().includes(s) ||
                (isPurchase ? (r.vendor_name || "") : (r.customer_name || "")).toLowerCase().includes(s) ||
                (r[dateField] || "").toLowerCase().includes(s) ||
                (r.invoice_date || "").toLowerCase().includes(s) ||
                (r.remarks || "").toLowerCase().includes(s)
              );
              if (!filtered.length) {
                return <TableRow><TableCell colSpan={isPurchase ? 8 : 7} className="text-center py-8 text-muted-foreground">No matching {label.toLowerCase()}s found.</TableCell></TableRow>;
              }
              return filtered.map(r => (
              <TableRow key={r.id} data-testid={`${testid}-row-${r.id}`}>
                <TableCell>{r[dateField]}</TableCell>
                {isPurchase && <TableCell className="text-xs">{r.invoice_date || "-"}</TableCell>}
                <TableCell className="font-mono text-xs">
                  {r.invoice_no || "-"}
                  {!isPurchase && r.invoice_type === "CASH_MEMO" && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700 align-middle">CASH</span>
                  )}
                </TableCell>
                <TableCell>{isPurchase ? r.vendor_name : r.customer_name}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.sub_total)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.tax_total)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{money(r.total_amount)}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => viewDetail(r)} data-testid={`${testid}-view-${r.id}`}
                    className="p-1.5 rounded hover:bg-secondary mr-1" title="View Details">
                    <Eye size={16} />
                  </button>
                  <button onClick={() => openEditSale(r)} data-testid={`${testid}-edit-${r.id}`}
                    className="p-1.5 rounded hover:bg-secondary text-blue-600 mr-1" title={`Edit ${label}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => removeRow(r)} data-testid={`${testid}-delete-${r.id}`}
                    className="p-1.5 rounded hover:bg-destructive/10 text-destructive mr-1" title={`Delete ${label}`}>
                    <Trash size={16} />
                  </button>
                  {!isPurchase && (
                    <>
                      <button onClick={() => shareWhatsApp(r)} data-testid={`sale-whatsapp-${r.id}`}
                        className="p-1.5 rounded hover:bg-secondary text-[#25D366] mr-1" title="Share on WhatsApp">
                        <WhatsappLogo size={16} weight="fill" />
                      </button>
                      <button onClick={() => navG(`/invoice/${r.id}`)} data-testid={`sale-print-${r.id}`}
                        className="p-1.5 rounded hover:bg-secondary text-[hsl(24.6_95%_53.1%)]" title="Print / PDF">
                        <Printer size={16} />
                      </button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ));
            })()}
            {!rows.length && <TableRow><TableCell colSpan={isPurchase ? 8 : 7} className="text-center py-12 text-muted-foreground">No {isPurchase ? "purchases" : "invoices"} yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {isPurchase ? "Purchase" : "Invoice"} Details — {detailData?.invoice_no || "-"}
            </DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{isPurchase ? "Vendor" : "Customer"}</div>
                  <div className="font-semibold">{isPurchase ? detailData.vendor?.vendor_name : detailData.customer?.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{isPurchase ? detailData.vendor?.mobile : detailData.customer?.mobile}</div>
                  <div className="text-xs text-muted-foreground">{isPurchase ? detailData.vendor?.state : detailData.customer?.state}</div>
                  {(isPurchase ? detailData.vendor?.gst_no : detailData.customer?.gst_no) &&
                    <div className="text-xs">GSTIN: {isPurchase ? detailData.vendor?.gst_no : detailData.customer?.gst_no}</div>}
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{isPurchase ? "Entry Date" : "Date"}</div>
                  <div className="font-semibold">{detailData[dateField]}</div>
                  {isPurchase && detailData.invoice_date && (
                    <>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mt-2">Bill Date</div>
                      <div className="font-semibold">{detailData.invoice_date}</div>
                    </>
                  )}
                  <div className="mt-2">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${detailData.tax_type === "CGST_SGST" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                      {detailData.tax_type === "CGST_SGST" ? "CGST + SGST" : "IGST"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-border rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Item Description</TableHead>
                      <TableHead>HSN</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">GST%</TableHead>
                      {detailData.tax_type === "CGST_SGST" ? (<>
                        <TableHead className="text-right">CGST</TableHead>
                        <TableHead className="text-right">SGST</TableHead>
                      </>) : (
                        <TableHead className="text-right">IGST</TableHead>
                      )}
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailData.items || []).map((li, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{li.item_code || "-"}</TableCell>
                        <TableCell>{li.item_name || "-"} {li.brand && <span className="text-xs text-muted-foreground">({li.brand})</span>}</TableCell>
                        <TableCell className="text-xs">{li.hsn_code || "-"}</TableCell>
                        <TableCell className="text-right tabular-nums">{li.qty}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(li.price)}</TableCell>
                        <TableCell className="text-right">{li.gst_rate}%</TableCell>
                        {detailData.tax_type === "CGST_SGST" ? (<>
                          <TableCell className="text-right tabular-nums text-xs">{money(li.cgst || li.tax/2)}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs">{money(li.sgst || li.tax/2)}</TableCell>
                        </>) : (
                          <TableCell className="text-right tabular-nums text-xs">{money(li.igst || li.tax)}</TableCell>
                        )}
                        <TableCell className="text-right tabular-nums font-semibold">{money(li.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end">
                <div className="w-72 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Sub Total:</span><span className="tabular-nums">{money(detailData.sub_total)}</span></div>
                  {detailData.tax_type === "CGST_SGST" ? (<>
                    <div className="flex justify-between"><span>CGST:</span><span className="tabular-nums">{money(detailData.cgst_total ?? detailData.tax_total/2)}</span></div>
                    <div className="flex justify-between"><span>SGST:</span><span className="tabular-nums">{money(detailData.sgst_total ?? detailData.tax_total/2)}</span></div>
                  </>) : (
                    <div className="flex justify-between"><span>IGST:</span><span className="tabular-nums">{money(detailData.igst_total ?? detailData.tax_total)}</span></div>
                  )}
                  {!isPurchase && Math.abs(detailData.round_off || 0) > 0.001 && (
                    <div className="flex justify-between text-blue-700">
                      <span>Round Off:</span>
                      <span className="tabular-nums">{detailData.round_off > 0 ? "+" : "-"}{money(Math.abs(detailData.round_off))}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t-2 border-gray-900 pt-2 mt-2 font-display text-lg font-bold">
                    <span>Grand Total:</span><span className="tabular-nums">{money(detailData.total_amount)}</span>
                  </div>
                </div>
              </div>

              {detailData.remarks && <div className="text-sm"><b>Remarks:</b> {detailData.remarks}</div>}

              {!isPurchase && (
                <div className="flex justify-end pt-2">
                  <Button onClick={() => navG(`/invoice/${detailData.id}`)} className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
                    <Printer size={16} className="mr-1" /> Print / Save as PDF
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
